import type { TenantBusinessProfile, TenantConfig, TenantTier } from '../types/tenant';
import { isValidHex } from './color';

const TIERS: readonly TenantTier[] = ['free', 'standard', 'premium'];

const TENANT_ID_RE = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/;

export function isValidTenantId(id: string): boolean {
  return TENANT_ID_RE.test(id);
}

function expect(cond: unknown, msg: string): asserts cond {
  if (!cond) throw new Error(`Invalid tenant config: ${msg}`);
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function parseTenantConfig(raw: unknown): TenantConfig {
  expect(isRecord(raw), 'root must be an object');

  const { id, displayName, tagline, branding, tier, businessProfile } = raw;

  expect(typeof id === 'string' && isValidTenantId(id), '`id` must match /^[a-z0-9-]+$/');
  expect(typeof displayName === 'string' && displayName.length > 0, '`displayName` required');
  expect(
    tagline === undefined || typeof tagline === 'string',
    '`tagline` must be string if present',
  );
  expect(typeof tier === 'string' && TIERS.includes(tier as TenantTier), `tier must be one of ${TIERS.join(', ')}`);
  expect(isRecord(branding), '`branding` must be an object');

  const { logoGlyph, logoUrl, accentHex, accentForegroundHex, accentHoverHex } = branding;
  expect(typeof logoGlyph === 'string' && logoGlyph.length > 0, '`branding.logoGlyph` required');
  expect(
    logoUrl === undefined || typeof logoUrl === 'string',
    '`branding.logoUrl` must be string if present',
  );
  expect(
    typeof accentHex === 'string' && isValidHex(accentHex),
    '`branding.accentHex` must be #RRGGBB',
  );
  expect(
    typeof accentForegroundHex === 'string' && isValidHex(accentForegroundHex),
    '`branding.accentForegroundHex` must be #RRGGBB',
  );
  expect(
    accentHoverHex === undefined || (typeof accentHoverHex === 'string' && isValidHex(accentHoverHex)),
    '`branding.accentHoverHex` must be #RRGGBB if present',
  );

  return {
    id,
    displayName,
    tagline,
    branding: {
      logoGlyph,
      logoUrl,
      accentHex,
      accentForegroundHex,
      accentHoverHex,
    },
    tier: tier as TenantTier,
    businessProfile: parseBusinessProfile(businessProfile),
  };
}

function parseBusinessProfile(raw: unknown): TenantBusinessProfile | undefined {
  if (raw === undefined || raw === null) return undefined;
  expect(isRecord(raw), '`businessProfile` must be an object if present');
  const {
    registeredName,
    tradeName,
    address,
    tin,
    defaultBranchCode,
    defaultTerminalId,
    atpNumber,
    atpDateIssued,
  } = raw;
  expect(typeof registeredName === 'string' && registeredName.length > 0, '`businessProfile.registeredName` required');
  expect(typeof address === 'string' && address.length > 0, '`businessProfile.address` required');
  expect(typeof tin === 'string' && tin.length > 0, '`businessProfile.tin` required');
  expect(typeof defaultBranchCode === 'string', '`businessProfile.defaultBranchCode` required');
  expect(typeof defaultTerminalId === 'string', '`businessProfile.defaultTerminalId` required');
  expect(tradeName === undefined || typeof tradeName === 'string', '`businessProfile.tradeName` must be string if present');
  expect(atpNumber === undefined || typeof atpNumber === 'string', '`businessProfile.atpNumber` must be string if present');
  expect(atpDateIssued === undefined || typeof atpDateIssued === 'string', '`businessProfile.atpDateIssued` must be string if present');
  return {
    registeredName,
    tradeName,
    address,
    tin,
    defaultBranchCode,
    defaultTerminalId,
    atpNumber,
    atpDateIssued,
  };
}

export async function loadTenantConfig(id: string, signal?: AbortSignal): Promise<TenantConfig> {
  if (!isValidTenantId(id)) {
    throw new Error(`Invalid tenant id: ${JSON.stringify(id)}`);
  }
  
  const cacheKey = `cf_tenant_config_${id}`;
  const cached = localStorage.getItem(cacheKey);
  if (cached) {
    try {
      const parsed = JSON.parse(cached);
      return parseTenantConfig(parsed);
    } catch (e) {
      localStorage.removeItem(cacheKey);
    }
  }

  const res = await fetch(`/tenants/${id}/config.json`, { signal, credentials: 'omit' });
  if (!res.ok) {
    throw new Error(`Tenant '${id}' config not found (HTTP ${res.status})`);
  }
  const raw: unknown = await res.json();
  const parsed = parseTenantConfig(raw);
  
  localStorage.setItem(cacheKey, JSON.stringify(raw));
  return parsed;
}

export async function loadTenantManifest(signal?: AbortSignal): Promise<string[]> {
  const cacheKey = 'cf_tenant_manifest';
  const cached = localStorage.getItem(cacheKey);
  if (cached) {
    try {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed) && parsed.every((x) => typeof x === 'string' && isValidTenantId(x))) {
        return parsed;
      }
    } catch (e) {
      localStorage.removeItem(cacheKey);
    }
  }

  const res = await fetch('/tenants/manifest.json', { signal, credentials: 'omit' });
  if (!res.ok) throw new Error(`Tenant manifest missing (HTTP ${res.status})`);
  const raw: unknown = await res.json();
  if (!Array.isArray(raw) || !raw.every((x) => typeof x === 'string' && isValidTenantId(x))) {
    throw new Error('Tenant manifest must be an array of tenant ids');
  }
  
  localStorage.setItem(cacheKey, JSON.stringify(raw));
  return raw;
}
